import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { AdminDeliveryTerm, AttributesService, DeliveryTermVariant } from '../../../_services/attributes.service';
import { AlertService } from '../../../_services/alert.service';
import { UserService } from '../../../_services/users.service';
import { ConfirmDialogComponent } from '../../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { Kind, canDeleteDeliveryTerms, deliveryTermOrder, slugKind } from '../delivery-terms.component';
import { RecordNavComponent, confirmLeave } from '../../../../Shared/_components/record-nav/record-nav.component';

// Create (/delivery-terms/create) or edit (/delivery-terms/edit/:id) one
// delivery term: pick its type (working-day range, call to arrange, other),
// the days, and the description customers see, with a live preview of the
// product page and checkout. Editing also lists the variants it changes.
@Component({
    selector: 'app-delivery-term-edit',
    imports: [CommonModule, FormsModule, RouterLink, MainLayoutComponent, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, RecordNavComponent],
    templateUrl: './delivery-term-edit.component.html'
})
export class DeliveryTermEditComponent implements OnInit {
  private attributesService = inject(AttributesService);
  private alertService = inject(AlertService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  readonly Math = Math;
  readonly canDelete = canDeleteDeliveryTerms(this.userService.isLoggedIn());
  readonly kinds: { key: Kind; label: string; hint: string; icon: string }[] = [
    { key: 'RANGE', label: 'Working days', hint: 'A range, e.g. 3–5 days', icon: 'date_range' },
    { key: 'CALL', label: 'Call to arrange', hint: 'Customer calls to book', icon: 'call' },
    { key: 'OTHER', label: 'Other', hint: 'No estimate at checkout', icon: 'more_horiz' },
  ];

  loading = true;
  notFound = false;
  terms: AdminDeliveryTerm[] = [];
  editing: AdminDeliveryTerm | null = null;
  variants: DeliveryTermVariant[] = [];
  variantsLoading = false;

  kind: Kind = 'RANGE';
  from: number | null = 3;
  to: number | null = 5;
  otherSlug = '';
  description = '';
  descriptionTouched = false;
  saving = false;

  // What was loaded, so Save only lights up once something changed.
  private original = '';

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id')) || null;
    this.attributesService.getAdminDeliveryTerms().subscribe({
      next: (rows) => {
        this.terms = rows;
        this.loading = false;
        if (id) {
          const term = rows.find((r) => r.id === id);
          if (!term) {
            this.notFound = true;
            return;
          }
          this.fillFrom(term);
          if (term.variants) this.loadVariants(term.id);
        } else {
          this.description = this.suggestedDescription;
        }
        this.original = this.snapshot;
      },
      error: () => {
        this.loading = false;
        this.alertService.alert('Could not load delivery terms', 'error');
      },
    });
  }

  private fillFrom(term: AdminDeliveryTerm): void {
    this.editing = term;
    this.kind = slugKind(term.deliveryTermSlug);
    if (this.kind === 'RANGE') {
      const [a, b] = term.deliveryTermSlug.split('-').map(Number);
      this.from = a;
      this.to = b;
    }
    this.otherSlug = this.kind === 'OTHER' ? term.deliveryTermSlug : '';
    this.description = term.deliveryTermDesc;
    this.descriptionTouched = true;
  }

  private loadVariants(id: number): void {
    this.variantsLoading = true;
    this.attributesService.getDeliveryTermVariants(id).subscribe({
      next: (rows) => {
        this.variants = rows;
        this.variantsLoading = false;
      },
      error: () => (this.variantsLoading = false),
    });
  }

  private get snapshot(): string {
    return `${this.slug}|${this.description.trim()}`;
  }

  get dirty(): boolean {
    return this.snapshot !== this.original;
  }

  // ---- Previous / next (list order) ----
  get ordered(): AdminDeliveryTerm[] {
    return [...this.terms].sort(deliveryTermOrder);
  }

  get position(): number {
    return this.editing ? this.ordered.findIndex((t) => t.id === this.editing!.id) : -1;
  }

  get prev(): AdminDeliveryTerm | null {
    return this.position > 0 ? this.ordered[this.position - 1] : null;
  }

  get next(): AdminDeliveryTerm | null {
    const i = this.position;
    return i >= 0 && i < this.ordered.length - 1 ? this.ordered[i + 1] : null;
  }

  goTo(t: AdminDeliveryTerm | null): void {
    if (!t || this.saving) return;
    confirmLeave(this.dialog, this.dirty, this.editing?.deliveryTermDesc ?? '', t.deliveryTermDesc).subscribe((ok) => {
      if (ok) this.router.navigate(['/delivery-terms/edit', t.id]);
    });
  }

  // ---- Form ----
  setKind(kind: Kind): void {
    this.kind = kind;
    this.syncDescription();
  }

  step(side: 'from' | 'to', by: number): void {
    if (side === 'from') this.from = Math.max(1, (this.from || 0) + by);
    else this.to = Math.max(1, (this.to || 0) + by);
    this.syncDescription();
  }

  // Keeps the description in step with the days until it's typed over.
  syncDescription(): void {
    if (!this.descriptionTouched) this.description = this.suggestedDescription;
  }

  onDescriptionTyped(value: string): void {
    this.description = value;
    this.descriptionTouched = true;
  }

  useSuggested(): void {
    this.description = this.suggestedDescription;
    this.descriptionTouched = false;
  }

  get suggestedDescription(): string {
    if (this.kind === 'RANGE') return this.from && this.to ? `Estimated delivery is ${this.from}-${this.to} working days` : '';
    if (this.kind === 'CALL') return 'Call us to arrange delivery';
    return '';
  }

  get slug(): string {
    if (this.kind === 'RANGE') return this.from && this.to ? `${this.from}-${this.to}` : '';
    if (this.kind === 'CALL') return 'call';
    return this.otherSlug.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  get formError(): string | null {
    if (this.kind === 'RANGE') {
      if (!this.from || !this.to || this.from < 1 || this.to < 1) return 'Enter both day numbers (1 or more)';
      if (this.from > this.to) return '"From" must not be more than "To"';
    }
    if (!this.slug) return 'Enter a slug';
    const clash = this.terms.find((t) => t.deliveryTermSlug === this.slug && t.id !== this.editing?.id);
    if (clash) return `"${this.slug}" is already used by "${clash.deliveryTermDesc}"`;
    if (!this.description.trim()) return 'Enter a description';
    return null;
  }

  // The day bar's scale: the longest range there is, this one included.
  get maxDays(): number {
    const ends = this.terms.map((t) => (slugKind(t.deliveryTermSlug) === 'RANGE' ? Number(t.deliveryTermSlug.split('-')[1]) : 0));
    return Math.max(10, this.to || 0, ...ends);
  }

  save(): void {
    if (this.formError || this.saving) return;
    this.saving = true;
    const name = this.description.trim();
    const request = this.editing
      ? this.attributesService.updateDeliveryTerm(this.editing.id, name, this.slug)
      : this.attributesService.createDeliveryTerm(name, this.slug);
    request.subscribe({
      next: (saved: any) => {
        this.saving = false;
        this.alertService.alert(this.editing ? 'Delivery term updated' : 'Delivery term created', 'success');
        const id = this.editing?.id ?? saved?.id;
        this.router.navigate(['/delivery-terms'], id ? { queryParams: { selected: id } } : {});
      },
      error: (error) => {
        this.saving = false;
        this.alertService.alert(error?.error?.message || 'Could not save the delivery term', 'error');
      },
    });
  }

  remove(): void {
    const term = this.editing;
    if (!term || !this.canDelete || term.variants) return;
    this.dialog
      .open(ConfirmDialogComponent, {
        data: { title: 'Delete delivery term', message: `Delete "${term.deliveryTermDesc}"? No variants use it.` },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result?.confirmation) return;
        this.attributesService.deleteDeliveryTerm(term.id).subscribe({
          next: () => {
            this.alertService.alert('Delivery term deleted', 'success');
            this.router.navigate(['/delivery-terms']);
          },
          error: (error) => this.alertService.alert(error?.error?.message || 'Could not delete the delivery term', 'error'),
        });
      });
  }
}
